import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import {usePropState} from "../utils";
import {useForm, Yup} from "../forms/form";
import {Block, CustomSwitch, FormGroup, Select} from "../components";
import {Col, Row} from "react-bootstrap";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";

export default function AggregationForm({refreshKey, parentFormRef, ...props}) {
  const { t } = useTranslation();
  const aggregationOptions = props.aggregationOptions;
  const [groupByAttributes, setGroupByAttributes] = useState([]);
  const [groupByOptions, setGroupByOptions] = useState({});
  const [metricsAttributes, setMetricsAttributes] = useState([]);
  const [disabled,] = usePropState(props.disabled);
  const [aggregate,] = usePropState(props.aggregate);
  const [dataType,] = usePropState(props.dataType)

  const {formData, setFormData, getInputProps, validated} = useForm({
    defaultObject: aggregate,
    initialValues: {
      use_aggregate: (obj) => !isEmpty(obj),
      group_by: (obj) => obj?.group_by?.map(el => {
        return Array.isArray(el) ? el[0] : el
      }) || [],
      metrics: [],
      group_by_options: (obj) => obj?.group_by?.reduce((acc, val) => {
        if (Array.isArray(val)) {
          acc[val[0]] = {[val[1][0]]: val[1][1]}
        }
        return acc
      }, {}) || {},
    },
    validationSchema: Yup.object().shape({
      group_by: Yup.array().when('use_aggregate', {
        is: true,
        then: Yup.array(Yup.string()).min(1).required()
      }),
      metrics: Yup.array().when('use_aggregate', {
        is: true,
        then: Yup.array(Yup.string()).min(1).required()
      })
    }),
    disabled: disabled,
    parentFormRef: parentFormRef,
    preProcess: (data) => {
      if (data.use_aggregate) {
        return {
          group_by: data.group_by.map(gby_item => {
            if (data.group_by_options.hasOwnProperty(gby_item)) {
              return Object.keys(data.group_by_options[gby_item]).reduce((acc, option) => {
                return [...acc, [option, data.group_by_options[gby_item][option]]];
              }, [gby_item]);
            } else {
              return gby_item
            }
          }),
          metrics: data.metrics
        }
      } else {
        return {}
      }
    }
  })

  useEffect(() => {
    setFormData(aggregate);
  }, [aggregate]);

  const getGroupByOptions = (fullOptions) => {
    return fullOptions.reduce((acc, val) => {
      if (Array.isArray(val)) {
        acc[val[0]] = val[1];
      }
      return acc
    }, {})
  }

  useEffect(() => {
    if (aggregationOptions?.hasOwnProperty(dataType)) {
      setGroupByAttributes(aggregationOptions[dataType].group_by.map(el => (Array.isArray(el) ? el[0] : el)));
      setMetricsAttributes(aggregationOptions[dataType].metrics);
      setGroupByOptions(getGroupByOptions(aggregationOptions[dataType].group_by));
    }
  }, [aggregationOptions, dataType]);

  useEffect(() => {
    getInputProps('group_by').onChange(formData.group_by.filter(x => groupByAttributes.includes(x)));
  }, [groupByAttributes]);

  return <>
    <FormGroup input={CustomSwitch} label={t("aggregate.useAggregate")} {...getInputProps("use_aggregate")}/>
    { formData.use_aggregate && <Row>
      <Block as={Col} title={t("aggregate.group_by")}>
        <FormGroup {...getInputProps("group_by")}>
          <Select isMulti options={groupByAttributes} />
        </FormGroup>
        <OptionsForm groupByOptions={groupByOptions} show={formData.group_by} validated={validated} {...getInputProps("group_by_options")}/>
      </Block>
      <Block as={Col} title={t("aggregate.metrics")}>
        <FormGroup {...getInputProps("metrics")}>
          <Select isMulti options={metricsAttributes} />
        </FormGroup>
      </Block>
    </Row>}
  </>
}

function OptionsForm(props) {

  const {name, errors, disabled, onChange} = props
  const [value, setValue] = usePropState(props.value);
  const [groupByOptions, setGroupByOptions] = usePropState(props.groupByOptions);
  const [show, setShow] = usePropState(props.show);
  const [validated, setValidated] = usePropState(props.validated);

  const onOptionChange = (attribute, option, data) => {
    onChange(merge(value, {[attribute]: {[option]: data}}));
  };

  const getOptionValue = (_value, attribute, option) => (get(_value, [attribute, option], ''));

  return <>
    { Object.keys(groupByOptions).map((attribute) => {
      return show.includes(attribute) && groupByOptions[attribute].map((option) => (
        <FormGroup
          key={`${attribute}.${option}`}
          input="number"
          label={`${attribute}.${option}`}
          onChange={(e) => onOptionChange(attribute, option, e.target.value)}
          value={getOptionValue(value, attribute, option)}
          isValid={validated && !!getOptionValue(value, attribute, option)}
          isInvalid={validated && !getOptionValue(value, attribute, option)}
          errors={validated && !getOptionValue(value, attribute, option) ? ['This field is required'] : []}
          name={`${attribute}.${option}`}
        />
      ))
    })}
  </>
}
